import { api_configuration } from "../../common";
import api_cs = api_configuration.types.client_state.alan_api;
import DestructorTree from "../DestructorTree";
import { updateEntriesSelection } from "./selection";
import { removeResultEntry } from "./Query";
import * as Text from "../state/Text";
import { getCollectionRegistrations, registrationInitialized } from "../state/registerClientState";
import * as requery from "../state 2 interface/reQuery";
import { None, ScalarProvider, WeakCache, createVariable, createExpressionBuilder } from "../../../../../stream";
import { toStream } from "../toStream";
import { entriesWithKey } from "./Collection";
import {
	register,
	subscriptionPending,
	getCollectionKeyRegistration,
	registerObjectPathTail,
	registerStategroup,
	registerText,
} from "../state/registerClientState";
import { Failed } from "../Status";
import ClientStateObjectProxy from "./ClientStateObjectProxy";
import { iCollection } from "../state/Collection";
import { DestructorSet, addChild } from "../../../../../destructables";

export function removeQueryEntryDependencies(
	query_entry_status: api_cs.Cquery_entry_status,
	requery_needed: boolean
): void {
	if (query_entry_status.destroyed) {
		return;
	}
	const query = query_entry_status.input.query();
	const has_result = query.properties.has_result.state;
	const position = query_entry_status.input.position().variant;
	switch (position.name) {
		case "result entry":
			switch (position.definition.properties.is_selected.state.name) {
				case "yes": {
					const selected_entry = position.definition.properties.is_selected.state.node.properties.selected_entry.ref;
					position.definition.properties.is_selected.set("no", {});
					selected_entry.parent.properties.selected_entries.delete(selected_entry.key);
					break;
				}
				case "no":
			}
			removeResultEntry(position.definition);
			if (requery_needed) {
				requery.execute(position.definition.parent.parent);
			}
			break;
		case "selected entry": {
			const selected_entry = position.definition;
			switch (has_result.name) {
				case "yes":
					for (const entry of has_result.node.properties.entries.values()) {
						const is_selected = entry.properties.is_selected.state;
						switch (is_selected.name) {
							case "yes":
								if (is_selected.node.properties.selected_entry.ref === selected_entry) {
									entry.properties.is_selected.set("no", {});
								}
								break;
							case "no":
						}
					}
					break;
				case "no":
			}
			selected_entry.parent.properties.selected_entries.delete(selected_entry.key);
			break;
		}
	}
	updateEntriesSelection(query);
	switch (has_result.name) {
		case "yes":
			has_result.node.set_total_amount_of_entries(has_result.node.properties.total_amount_of_entries.value - 1);
			break;
		case "no":
	}
}

export function isMarkedForDeletion(entry: api_cs.Centries__collection): boolean {
	switch (entry.properties.location.state.name) {
		case "remote": {
			const $ = entry.properties.location.state.node;
			switch ($.properties.marked_for_deletion.state.name) {
				case "no":
					return false;
				case "yes":
					return true;
			}
			break;
		}
		case "local":
			return false;
	}
}

export function getKeyValue(entry: api_cs.Centries__collection): string | undefined {
	return Text.getValue(entry.properties.key.ref);
}

export function getKeyBackendValue(entry: api_cs.Centries__collection): string | undefined {
	return Text.getBackendValue(entry.properties.key.ref);
}

export function getIndex(entry: api_cs.Centries__collection): string | undefined {
	switch (entry.properties.location.state.name) {
		case "local":
			// Local entries are indexed by their internal key, because that is guaranteed to be unique
			return entry.key;
		case "remote":
			// Remote entries are indexed by their backend key, because that is unique on the server
			return getKeyBackendValue(entry);
		default:
			unexpectedState(entry.properties.location.state);
	}
}

export function keyInitialized(entry: api_cs.Centries__collection): Promise<void> {
	const value = Text.getValue(entry.properties.key.ref);
	if (value === undefined) {
		return Promise.resolve();
	} else {
		const key_registration = getCollectionRegistrations(entry.parent).keys.get(value);
		if (key_registration) {
			return registrationInitialized(key_registration);
		} else {
			return Promise.resolve();
		}
	}
}

class EntryInterface extends ClientStateObjectProxy {
	readonly parent: iCollection;
	readonly keyUnique: ScalarProvider<"yes" | "no">;

	constructor(readonly entry: api_cs.Centries__collection) {
		super();
		this.parent = iCollection(entry.parent);
		this.keyUnique = createVariable(
			toStream(entry.properties.location)
				.stateSwitch({
					remote: () =>
						toStream(entry.properties.key.ref.properties.is_editable).stateSwitch({
							no: () => false,
							yes: ($) =>
								toStream($.properties.modified).stateSwitch({
									no: () => false,
									yes: () => true,
								})(),
						})(),
					local: () => true,
				})
				.pipe((modified) => {
					if (!modified) return "yes" as "yes" | "no";
					return createExpressionBuilder(Text.value(entry.properties.key.ref)).pipe((key) => {
						if (key === Failed) return "yes";
						return subscriptionPending(getCollectionKeyRegistration(entry.parent, key)).pipe((pending) => {
							if (pending) return "no";
							return entriesWithKey(entry.parent, key)
								.pipe((entries) => (entries === None ? 0 : entries.size))
								.pipe((amount_of_entries) => (amount_of_entries > 1 ? "no" : "yes"))();
						})();
					})();
				})
				.pipe((unique) => {
					entry.properties.key.inferences.key().properties.key_unique.ensure(unique, {});
					return unique;
				})(),
			false
		);
	}

	private _registration_destructor: DestructorSet<DestructorTree> | undefined;
	register(destructor: DestructorTree) {
		register(this.entry, destructor);
		if (this._registration_destructor === undefined || this._registration_destructor.destroyed) {
			this._registration_destructor = new DestructorSet(false);
			const _destructor = new DestructorTree();
			addChild(this._registration_destructor, _destructor);
			for (const graph of this.parent.graphs) {
				const context_node = registerObjectPathTail(
					graph.ordering_stategroup_path,
					this.entry.properties.node,
					destructor
				)!;
				const ordering_stategroup = registerStategroup(graph.ordering_stategroup, context_node, destructor);
				const edge_state = ordering_stategroup.properties.states.get(graph.edge_state.key)!;
				registerText(graph.edge_reference, edge_state.properties.node, destructor);
			}
		}
		this._registration_destructor!.add(destructor);
	}
}

export type iEntry = EntryInterface;
export const iEntry = WeakCache<api_cs.Centries__collection, EntryInterface>((s) => new EntryInterface(s));
